import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';

const API_BASE = 'https://rbuymart.rbhardwaj.com/server/api/cart';

const getAuthHeaders = () => ({
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${localStorage.getItem('access_token')}`
});

export const fetchCart = createAsyncThunk('cart/fetchCart', async (userId, { rejectWithValue }) => {
    try {
        if (!userId) return rejectWithValue("User ID missing.");
        
        const response = await fetch(`${API_BASE}/get.php?userid=${userId}`, { 
            headers: getAuthHeaders() 
        });
        const data = await response.json();
        if (!response.ok) return rejectWithValue(data.message || "Failed to fetch cart data.");
        return data;
    } catch (error) {
        return rejectWithValue(error.message);
    }
});

export const addToCart = createAsyncThunk('cart/addToCart', async (payload, { dispatch, rejectWithValue }) => {
    try {
        const response = await fetch(`${API_BASE}/add.php`, {
            method: 'POST',
            headers: getAuthHeaders(),
            body: JSON.stringify(payload)
        });
        const data = await response.json();
        if (!response.ok) return rejectWithValue(data.message || "Failed to add item.");
        
        dispatch(fetchCart(payload.userid || payload.user_id));
        return data;
    } catch (error) {
        return rejectWithValue(error.message);
    }
});

export const updateCartQuantity = createAsyncThunk('cart/updateCartQuantity', async ({ cartId, quantity, userId }, { dispatch, rejectWithValue }) => {
    try {
        const response = await fetch(`${API_BASE}/update.php`, {
            method: 'POST',
            headers: getAuthHeaders(),
            body: JSON.stringify({ cart_id: cartId, quantity })
        });
        const data = await response.json();
        if (!response.ok) return rejectWithValue(data.message || "Failed to update quantity.");
        
        dispatch(fetchCart(userId));
        return data;
    } catch (error) {
        return rejectWithValue(error.message);
    }
});

export const removeFromCart = createAsyncThunk('cart/removeFromCart', async ({ cartId, userId }, { dispatch, rejectWithValue }) => {
    try {
        const response = await fetch(`${API_BASE}/remove.php`, {
            method: 'POST',
            headers: getAuthHeaders(),
            body: JSON.stringify({ cart_id: cartId })
        });
        const data = await response.json();
        if (!response.ok) return rejectWithValue(data.message || "Failed to remove item.");
        
        dispatch(fetchCart(userId));
        return data;
    } catch (error) {
        return rejectWithValue(error.message);
    }
});

export const applyCoupon = createAsyncThunk('cart/applyCoupon', async ({ code, orderTotal }, { rejectWithValue }) => {
    try {
        const response = await fetch(`${API_BASE}/validate_coupon.php`, {
            method: 'POST',
            headers: getAuthHeaders(),
            body: JSON.stringify({ code, order_total: orderTotal })
        });
        
        const data = await response.json();

        if (!response.ok || data.status === 'error') {
            return rejectWithValue(data.message || "Invalid coupon scheme validation.");
        }
        
        // 🚀 FIXED: अगर API कोड रिटर्न नहीं करती, तो भेजे गए 'code' को रिस्पॉन्स में इंजेक्ट करें
        return { ...data, code: data.code || code };
    } catch (error) {
        return rejectWithValue(error.message || "Network validation handshake failure.");
    }
});

const cartSlice = createSlice({
    name: 'cart',
    initialState: {
        items: [],
        subtotal: 0,
        gstTotal: 0,
        grandTotal: 0,
        coupon: null,
        discount: 0,
        loading: false,
        error: null
    },
    reducers: {
        clearCoupon: (state) => {
            state.grandTotal = state.subtotal + state.gstTotal;
            state.coupon = null;
            state.discount = 0;
            state.error = null;
        }
    },
    extraReducers: (builder) => {
        const handleCartFulfilled = (state, action) => {
            state.loading = false;
            state.error = null;
            state.items = Array.isArray(action.payload.items) ? action.payload.items : [];
            state.subtotal = Number(action.payload.subtotal) || 0;
            state.gstTotal = Number(action.payload.gst_total) || 0;
            
            const baseTotal = state.subtotal + state.gstTotal;
            state.grandTotal = Math.max(0, baseTotal - state.discount);
        };

        builder
            .addCase(fetchCart.pending, (state) => { state.loading = true; state.error = null; })
            .addCase(addToCart.pending, (state) => { state.loading = true; state.error = null; })
            .addCase(updateCartQuantity.pending, (state) => { state.loading = true; state.error = null; })
            .addCase(removeFromCart.pending, (state) => { state.loading = true; state.error = null; })
            
            .addCase(fetchCart.fulfilled, handleCartFulfilled)
            .addCase(addToCart.fulfilled, (state, action) => {
                if (action.payload && action.payload.items !== undefined) {
                    handleCartFulfilled(state, action);
                } else {
                    state.loading = false; 
                }
            })
            .addCase(updateCartQuantity.fulfilled, (state) => { state.loading = false; })
            .addCase(removeFromCart.fulfilled, (state) => { state.loading = false; })
            
            .addCase(fetchCart.rejected, (state, action) => { state.loading = false; state.error = action.payload; })
            .addCase(addToCart.rejected, (state, action) => { state.loading = false; state.error = action.payload; })
            .addCase(updateCartQuantity.rejected, (state, action) => { state.loading = false; state.error = action.payload; })
            .addCase(removeFromCart.rejected, (state, action) => { state.loading = false; state.error = action.payload; })
            
            .addCase(applyCoupon.fulfilled, (state, action) => {
                state.coupon = action.payload.code;
                const parsedDiscount = Number(action.payload.discount) || 0;
                state.discount = parsedDiscount;
                
                const baseTotal = state.subtotal + state.gstTotal;
                state.grandTotal = Math.max(0, baseTotal - parsedDiscount);
                state.error = null;
            })
            .addCase(applyCoupon.rejected, (state, action) => {
                state.error = action.payload;
                state.coupon = null;
                state.discount = 0;
                state.grandTotal = state.subtotal + state.gstTotal;
            });
    }
});

export const { clearCoupon } = cartSlice.actions;
export default cartSlice.reducer;